import React, { useState } from 'react';
import { FaCalendarAlt, FaWater, FaPaw, FaTractor, FaSeedling, FaMapMarkerAlt, FaArrowUp, FaArrowDown, FaArrowRight, FaEye } from 'react-icons/fa';
import { MdFlightTakeoff } from 'react-icons/md';
import CropStatistics from './CropStatistics';
import '../styles/resources.css';

const ResourceDashboard = () => {
  const [resources, setResources] = useState([
    {
      id: 1,
      type: 'tractor',
      name: 'John Deere 5075E',
      status: 'available',
      location: 'Village A',
      nextAvailable: 'Now',
      icon: <FaTractor />
    },
    {
      id: 2,
      type: 'tractor',
      name: 'Kubota L4701',
      status: 'in-use',
      location: 'Village B',
      nextAvailable: 'May 15, 2023',
      icon: <FaTractor />
    },
    {
      id: 3,
      type: 'drone',
      name: 'DJI Agras T30',
      status: 'maintenance',
      location: 'Central Station',
      nextAvailable: 'May 18, 2023',
      icon: <MdFlightTakeoff />
    },
    {
      id: 4,
      type: 'drone',
      name: 'Parrot Bluegrass',
      status: 'available',
      location: 'Village C',
      nextAvailable: 'Now',
      icon: <MdFlightTakeoff />
    }
  ]);

  const [aquiferLevels, setAquiferLevels] = useState([
    { id: 1, name: 'North Aquifer', level: 78, trend: 'stable', lastUpdated: '2 hours ago' },
    { id: 2, name: 'South Aquifer', level: 62, trend: 'decreasing', lastUpdated: '1 hour ago' },
    { id: 3, name: 'East Aquifer', level: 85, trend: 'increasing', lastUpdated: '30 minutes ago' }
  ]);

  const [wildlifePaths, setWildlifePaths] = useState([
    { id: 1, name: 'Deer Migration Path', status: 'active', lastActivity: '1 day ago' },
    { id: 2, name: 'Pollinator Corridor', status: 'planned', lastActivity: 'N/A' },
    { id: 3, name: 'Bird Sanctuary Zone', status: 'active', lastActivity: '2 hours ago' }
  ]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'available': return 'var(--accent-green)';
      case 'in-use': return 'var(--accent-yellow)';
      case 'maintenance': return 'var(--accent-red)';
      case 'active': return 'var(--accent-green)';
      case 'planned': return 'var(--accent-blue)';
      default: return 'var(--text-color)';
    }
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'increasing': return <FaArrowUp />;
      case 'decreasing': return <FaArrowDown />;
      case 'stable': return <FaArrowRight />;
      default: return null;
    }
  };

  const handleBookResource = (id) => {
    // In a real app, this would open a booking modal or form
    alert(`Booking resource ${id}`);
  };

  const handleViewOnMap = (id) => {
    // In a real app, this would focus the map on the wildlife path
    alert(`Viewing wildlife path ${id} on map`);
  };

  return (
    <div className="resource-dashboard">
      <div className="section-header">
        <h2>Community Resource Dashboard</h2>
      </div>
      
      <div className="resource-sections">
        <div className="resource-section">
          <h3>
            <FaSeedling />
            DMV Agricultural Data
          </h3>
          <CropStatistics />
        </div>
        
        <div className="resource-section">
          <h3>
            <FaTractor />
            Equipment Booking System
          </h3>
          <div className="resource-cards">
            {resources.map(resource => (
              <div key={resource.id} className="resource-card">
                <div className="resource-icon">{resource.icon}</div>
                <div className="resource-details">
                  <h4>{resource.name}</h4>
                  <div className="resource-meta">
                    <span 
                      className="resource-status" 
                      style={{ color: getStatusColor(resource.status) }}
                    >
                      {resource.status.toUpperCase()}
                    </span>
                    <span className="resource-location">
                      <FaMapMarkerAlt style={{ marginRight: '4px' }} />
                      {resource.location}
                    </span>
                  </div>
                  <div className="resource-availability">
                    <FaCalendarAlt /> Next Available: {resource.nextAvailable}
                  </div>
                </div>
                <button 
                  className="book-button"
                  disabled={resource.status !== 'available'}
                  onClick={() => handleBookResource(resource.id)}
                >
                  Book
                </button>
              </div>
            ))}
          </div>
        </div>
        
        <div className="resource-section">
          <h3>
            <FaWater />
            Aquifer Recharge Monitoring
          </h3>
          <div className="aquifer-cards">
            {aquiferLevels.map(aquifer => (
              <div key={aquifer.id} className="aquifer-card">
                <div className="aquifer-header">
                  <h4>{aquifer.name}</h4>
                  <span className="last-updated">Updated: {aquifer.lastUpdated}</span>
                </div>
                <div className="aquifer-level-container">
                  <div 
                    className="aquifer-level-bar"
                    style={{ 
                      width: `${aquifer.level}%`,
                      backgroundColor: aquifer.level > 70 ? 'var(--accent-green)' : 
                                      aquifer.level > 50 ? 'var(--accent-yellow)' : 
                                      'var(--accent-red)'
                    }}
                  ></div>
                </div>
                <div className="aquifer-details">
                  <span className="aquifer-percentage">{aquifer.level}%</span>
                  <span 
                    className="aquifer-trend"
                    style={{ 
                      color: aquifer.trend === 'increasing' ? 'var(--accent-green)' : 
                             aquifer.trend === 'decreasing' ? 'var(--accent-red)' : 
                             'var(--accent-yellow)'
                    }}
                  >
                    Trend: {aquifer.trend} {getTrendIcon(aquifer.trend)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="resource-section">
          <h3>
            <FaPaw />
            Wildlife Migration Path Design
          </h3>
          <div className="wildlife-cards">
            {wildlifePaths.map(path => (
              <div key={path.id} className="wildlife-card">
                <div className="wildlife-icon">
                  <FaPaw />
                </div>
                <div className="wildlife-details">
                  <h4>{path.name}</h4>
                  <div className="wildlife-meta">
                    <span 
                      className="wildlife-status"
                      style={{ color: getStatusColor(path.status) }}
                    >
                      {path.status.toUpperCase()}
                    </span>
                    {path.lastActivity !== 'N/A' && (
                      <span className="wildlife-activity">
                        Last activity: {path.lastActivity}
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  className="view-button"
                  onClick={() => handleViewOnMap(path.id)}
                >
                  <FaEye style={{ marginRight: '4px' }} />
                  View on Map
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceDashboard; 